import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NotificationsAPI } from "../api/notifications";
import type {
  NotificationCandidate,
  NotificationSettingPatch,
  NotificationTopicView,
} from "../types";

export const notificationKeys = {
  all: ["notifications"] as const,
  topics: (clientId: string) =>
    [...notificationKeys.all, "topics", clientId] as const,
  preview: (clientId: string, topic: string) =>
    [...notificationKeys.all, "preview", clientId, topic] as const,
};

/** The company's notification settings and who it can send them to. */
export function useNotificationTopics(queryOptions?: { enabled?: boolean }) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<{
    topics: NotificationTopicView[];
    candidates: NotificationCandidate[];
  }>({
    queryKey: notificationKeys.topics(clientId),
    queryFn: () => NotificationsAPI.listTopics(clientId),
    staleTime: 30 * 1000,
    enabled: !!clientId && queryOptions?.enabled !== false,
  });
}

export function useUpdateNotificationSetting() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      topic,
      patch,
    }: {
      topic: string;
      patch: NotificationSettingPatch;
    }) => NotificationsAPI.updateSetting(clientId, topic, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.topics(clientId),
      });
    },
  });
}

/** What a digest would say now — read-only, nothing is sent. */
export function useNotificationPreview(
  topic: string,
  queryOptions?: { enabled?: boolean },
) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery({
    queryKey: notificationKeys.preview(clientId, topic),
    queryFn: () => NotificationsAPI.preview(clientId, topic),
    staleTime: 60 * 1000,
    enabled: !!clientId && !!topic && queryOptions?.enabled !== false,
  });
}
